import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CaseInfo, useApiReload } from '../../context/apiRelaod';
import { useAuth } from '../../context/authContext';
import {
	CaseList,
	CaseUpdate,
	DeleteSoap,
	PatientFetch,
	SoapList,
	UserFormList,
	viewModelPdf,
} from '../../services/apiService';
import TableTemplate from '../../templates/common/TableParts.template';
import { AudioFileDataWithoutBlob, getAllData } from '../../utils/indexedDB';
import { dateFormat } from '../../utils/priceFormat.util';
import Button from '../../components/ui/Button';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../../components/ui/Modal';
import classNames from 'classnames';

interface Patient {
	id: string;
	name: string;
	emailId: string;
	MobileNo: string;
}

interface Case {
	id: string;
	timestamp: string;
	soapNotes: { id: string; note: string; timestamp: string }[];
}

const UserForm = () => {
	const { onLogout } = useAuth();
	const [caseData, setCaseData] = useState<any>();
	const { UserFormLists, UserFormResponse } = UserFormList();
	const { patientID, reloadNotes, setReloadNotes } = useApiReload();

	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { call: formViewPdf, loading: formviewPdfLoading } = viewModelPdf();

	const { Patient_call, Patient_loading, Patient_response, error } = PatientFetch();
	const columnHelper = createColumnHelper<any>();
	const [modalData, setModalData] = useState<{
		s3_bucket?: string;
		s3_key?: string;
		formId?: string;
		noteId?: string;
	} | null>(null);
	const [activeButtonView, setActiveButtonView] = useState<string>();
	const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
	const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);
	const [SoapResponseData, setSoapResponseData] = useState<any>();
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [noteToDelete, setNoteToDelete] = useState<string | null>(null);

	
	const handleViewPdf = (formUrl: any, formId: string, index: number,formCreate: boolean,needToUseOld: boolean,noteId: string) => {
		formUrl = JSON.parse(formUrl);
		formUrl.formId = formId;
		formUrl.forceCreate = formCreate;		
		formUrl.noteId = noteId;	
		setActiveButtonView(formId);
		setLoadingIndex(index);
		setModalData(formUrl);		
		formViewPdf(JSON.stringify(formUrl)).subscribe({
			next: (res: any) => {
				if (res?.data?.updated && !needToUseOld) {
					setUpdateModalOpen(true);
				} else {
					const byteArray = res?.data?.pdf?.data; // assuming 'data' contains the array of bytes
					const blob = new Blob([new Uint8Array(byteArray)], { type: 'application/pdf' });
					const url = URL.createObjectURL(blob);
					setUpdateModalOpen(false);
					window.open(url, '_blank');
					setLoadingIndex(null);
					setTimeout(() => {
						URL.revokeObjectURL(url);
					}, 5000);
				}
			},
			error: (error: any) => {
				setLoadingIndex(null);
				if (error?.status === 409) {
					onLogout(error?.status);
				}
				console.error('Error in Opening the PDF:', error);
			},
		});
	};
	const columns = [
		columnHelper.accessor('note_id', {
			header: 'Note Id',
		}),
		columnHelper.accessor('case_id', {
			header: 'Case Id',
		}),
		columnHelper.display({
			id: 'actions',
			header: () => <div className="text-right mr-2.5">Actions</div>,
			cell: ({ row }) => {
				const followUp = row.original;
				const idx = row.index;				
				return (
					<div className='w-full flex justify-end'>
					<Button
						variant='solid'
						isLoading={loadingIndex === idx}
						colorIntensity='500'
						className='font-medium text-white primary-bg'
						onClick={() => handleViewPdf(followUp.form_url, followUp.form_id,idx, false,false,followUp.note_id)}>
						View
					</Button>
					</div>
					
				);
			},
		}),
	];
	const table = useReactTable({
		columns,
		data: SoapResponseData?.data || [],
		getCoreRowModel: getCoreRowModel(),
	});

	useEffect(() => {
		UserFormLists().subscribe({
			next: (value: any) => {
				setSoapResponseData(value);
			},
			error: (error) => {
				if (error?.status === 409) {
					onLogout(error?.status);
				}
				console.error('An error occurred:', error);
			},
		});
	}, []);

	// Effect to reload notes when notifications change
	useEffect(() => {
		if (reloadNotes) {
			// Reset the reload flag
			setReloadNotes(false);
			
			// Reload user form list for current view without navigation
			UserFormLists().subscribe({
				next: (value: any) => {
					setSoapResponseData(value);
				},
				error: (error) => {
					if (error?.status === 409) {
						onLogout(error?.status);
					}
					console.error('An error occurred:', error);
				},
			});
		}
	}, [reloadNotes, setReloadNotes, onLogout]);

	return (
		<>
		<div className='min-h-screen bg-gray-50 dark:bg-slate-900'>
			<div className='container mx-auto px-4 pb-16 pt-6'>
				{/* Case Information Section */}
				{SoapResponseData && (
					<div className='mb-6 rounded-lg bg-white p-6 shadow-md dark:bg-slate-800'>
						{/* SOAP Notes Section */}
						<div className='mb-6'>
							{SoapResponseData?.data?.length > 0 ? (
								<div className='overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700'>
									<div className='max-h-full overflow-y-auto'>
										<TableTemplate
											table={table}
											hasFooter={false}
											isSticky={true}
											className='w-full'
										/>
									</div>
								</div>
							) : (
								<div className='rounded-lg border border-dashed border-gray-300 py-8 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400'>
									<p className='font-medium'>No SOAP notes found for this case</p>
									<p className='mt-1 text-sm'>
										Create a follow-up note to get started
									</p>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
		<Modal
				isOpen={isUpdateModalOpen}
				setIsOpen={setUpdateModalOpen}
				isCentered
				size='sm'
				closeOnOverlayClick={false}
				data={modalData}>
				<ModalHeader setIsOpen={setUpdateModalOpen}>Update Form</ModalHeader>
				<ModalBody className='pt-3'>
					<p className='mb-0 text-lg'>Do you want to regenerate form?</p>
				</ModalBody>
				<ModalFooter className='justify-end pt-3'>
					<Button
						variant='solid'
						color='gray'
						colorIntensity='500'
						className='cursor-pointer text-white'
						isLoading={formviewPdfLoading}
						onClick={() => {
							if (!modalData) return; // Prevent errors if modalData is null
							handleViewPdf(
								JSON.stringify({
									s3_bucket: modalData.s3_bucket,
									s3_key: modalData.s3_key,
								}),
								modalData.formId ?? '',
								1,
								false, // Do not regenerate
								true,  // Use old data
								modalData.noteId ?? '' // Ensure noteId is passed
							);
						}}>
						No
					</Button>
					<Button
						variant='solid'
						colorIntensity='600'
						className='primary-bg cursor-pointer text-white'
						isLoading={formviewPdfLoading}
						onClick={() => {
							if (!modalData) return;
							handleViewPdf(
								JSON.stringify({
									s3_bucket: modalData.s3_bucket,
									s3_key: modalData.s3_key,
								}),
								modalData.formId ?? '',
								1,
								true,  // Regenerate the form
								false, // Do not use old data
								modalData.noteId ?? ''
							);
						}}>
						Yes
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default UserForm;
